import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ListOrdered, RefreshCw, ArrowUpRight, ArrowDownRight, Store } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { getAdminAnalytics, getToken, clearToken } from "@/lib/api";

const RANGES = ["1W", "4W", "1Y", "MTD", "QTD", "YTD", "ALL"];

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_research-precision-1/artifacts/0d2556841c9f64b9_logo%20lighter.jpeg";

const gbp = (n) =>
  "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kgbp = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return "£" + (v / 1000).toFixed(1) + "k";
  return "£" + v.toFixed(2);
};
const dayLabel = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const dayLabelYr = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const now = new Date();
now.setHours(0, 0, 0, 0);

function rangeStart(range) {
  const d = new Date(now);
  if (range === "1W") d.setDate(d.getDate() - 6);
  else if (range === "4W") d.setDate(d.getDate() - 27);
  else if (range === "1Y") d.setDate(d.getDate() - 364);
  else if (range === "MTD") d.setDate(1);
  else if (range === "QTD") d.setMonth(Math.floor(d.getMonth() / 3) * 3, 1);
  else if (range === "YTD") d.setMonth(0, 1);
  else return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function computeMetric(series, range, key) {
  const start = rangeStart(range);
  const points = start ? series.filter((p) => new Date(p.date) >= start) : series.slice();
  const value = points.reduce((s, p) => s + Number(p[key] || 0), 0);

  let pct = null;
  if (points.length) {
    const len = points.length;
    const firstDate = new Date(points[0].date);
    const prevEnd = new Date(firstDate);
    prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd);
    prevStart.setDate(prevStart.getDate() - (len - 1));
    const prevPoints = series.filter((p) => {
      const d = new Date(p.date);
      return d >= prevStart && d <= prevEnd;
    });
    const prevValue = prevPoints.reduce((s, p) => s + Number(p[key] || 0), 0);
    if (prevValue > 0) pct = ((value - prevValue) / prevValue) * 100;
    else if (value > 0) pct = 100;
  }

  const firstD = points.length ? new Date(points[0].date) : now;
  const lastD = points.length ? new Date(points[points.length - 1].date) : now;
  const isToday = lastD.getTime() === now.getTime();
  const label = `${dayLabel(firstD)} – ${isToday ? "Today" : dayLabelYr(lastD)}`;

  return { points, value, pct, label };
}

// Smooth count-up hook
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const from = prev.current;
    const to = Number(target) || 0;
    prev.current = to;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const RangeTabs = ({ value, onChange }) => (
  <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="range-tabs">
    {RANGES.map((r) => (
      <button
        key={r}
        data-testid={`range-${r}`}
        onClick={() => onChange(r)}
        className="relative shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide transition-colors"
      >
        {value === r && (
          <motion.span
            layoutId="range-pill"
            className="absolute inset-0 rounded-full bg-gradient-to-b from-sky-400 to-blue-600 shadow-[0_0_20px_rgba(56,165,255,0.7)]"
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
          />
        )}
        <span className={`relative z-10 ${value === r ? "text-white" : "text-slate-400 hover:text-slate-200"}`}>{r}</span>
      </button>
    ))}
  </div>
);

const PctBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-lg px-2 py-1 text-xs font-bold ring-1 ${
        up
          ? "bg-emerald-500/10 text-emerald-400 ring-emerald-500/30 shadow-[0_0_14px_rgba(16,185,129,0.25)]"
          : "bg-rose-500/10 text-rose-400 ring-rose-500/30 shadow-[0_0_14px_rgba(244,63,94,0.25)]"
      }`}
    >
      <Icon className="h-3 w-3" />
      {up ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
};

const ReportChart = ({ title, metric, gradId }) => {
  const animated = useCountUp(metric.value);
  return (
    <div className="relative">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-300">{title}</p>
          <p className="mt-1 bg-gradient-to-br from-white to-sky-200 bg-clip-text font-display text-2xl font-bold text-transparent">{kgbp(animated)}</p>
          <p className="mt-0.5 text-[11px] text-slate-500">{metric.label}</p>
        </div>
        <PctBadge pct={metric.pct} />
      </div>
      <div className="stripe-chart mt-2 h-40 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={metric.points} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#56a5ff" stopOpacity={0.45} />
                <stop offset="100%" stopColor="#56a5ff" stopOpacity={0} />
              </linearGradient>
              <linearGradient id={`${gradId}-stroke`} x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#7cc3ff" />
                <stop offset="100%" stopColor="#e9f3ff" />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(148,163,184,0.1)" horizontal={false} />
            <XAxis dataKey="date" hide />
            <YAxis hide domain={[0, "dataMax"]} />
            <Tooltip
              formatter={(v) => [gbp(v), title]}
              labelFormatter={(l) => dayLabelYr(new Date(l))}
              contentStyle={{ background: "rgba(11,19,36,0.92)", border: "1px solid rgba(124,195,255,0.3)", borderRadius: 12, fontSize: 12, color: "#e2e8f0", boxShadow: "0 8px 30px rgba(0,0,0,0.5)" }}
              itemStyle={{ color: "#e2e8f0" }}
              labelStyle={{ color: "#94a3b8" }}
              cursor={{ stroke: "rgba(124,195,255,0.4)", strokeDasharray: "4 4" }}
            />
            <Area
              type="monotone"
              dataKey={metric.key}
              stroke={`url(#${gradId}-stroke)`}
              strokeWidth={2.5}
              fill={`url(#${gradId})`}
              dot={false}
              activeDot={{ r: 4, fill: "#fff", stroke: "#56a5ff", strokeWidth: 2 }}
              isAnimationActive
              animationDuration={900}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const TodayStat = ({ label, value, money, align }) => {
  const animated = useCountUp(value);
  const display = money ? gbp(animated) : Math.round(animated).toLocaleString("en-GB");
  return (
    <div className={align}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-2 bg-gradient-to-br from-white to-slate-300 bg-clip-text font-display text-xl font-bold text-transparent sm:text-2xl">{display}</p>
    </div>
  );
};

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("4W");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAdminAnalytics();
      setData(res);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); navigate("/control", { replace: true }); return; }
      toast.error("Could not load analytics.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/control", { replace: true }); return; }
    load();
  }, [load, navigate]);

  const logout = () => { clearToken(); navigate("/control", { replace: true }); };

  const series = data?.series || [];
  const gross = useMemo(() => ({ key: "gross", ...computeMetric(series, range, "gross") }), [series, range]);
  const net = useMemo(() => ({ key: "net", ...computeMetric(series, range, "net") }), [series, range]);
  const today = data?.today || { gross_volume: 0, payments: 0, customers: 0 };

  const bestSellers = useMemo(() => {
    const items = data?.item_sales || [];
    const start = rangeStart(range);
    const inRange = start ? items.filter((it) => it.date && new Date(it.date) >= start) : items;
    const map = {};
    for (const it of inRange) {
      const s = (map[it.name] = map[it.name] || { name: it.name, units: 0, revenue: 0 });
      s.units += Number(it.units || 0);
      s.revenue = Math.round((s.revenue + Number(it.revenue || 0)) * 100) / 100;
    }
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [data, range]);
  const totalUnits = bestSellers.reduce((s, x) => s + x.units, 0);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#060a14] text-slate-100">
      {/* ambient glow orbs */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-blue-600/25 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-20 h-72 w-72 rounded-full bg-sky-500/15 blur-[110px]" />
      <div className="pointer-events-none absolute bottom-0 -left-20 h-72 w-72 rounded-full bg-indigo-700/15 blur-[120px]" />

      {/* faint logo watermark, blended into the scene */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/3 z-0 h-[380px] w-[380px] -translate-x-1/2 overflow-hidden rounded-full opacity-[0.06] mix-blend-luminosity"
        style={{ WebkitMaskImage: "radial-gradient(circle, #000 42%, transparent 72%)", maskImage: "radial-gradient(circle, #000 42%, transparent 72%)" }}
        aria-hidden
      >
        <img src={LOGO} alt="" className="h-full w-full scale-[1.18] object-cover" />
      </div>

      {/* top bar */}
      <header className="sticky top-0 z-20 border-b border-white/5 bg-[#060a14]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3.5">
          <button onClick={() => navigate("/control/orders")} data-testid="nav-to-orders" className="grid h-9 w-9 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-white/5" title="Orders">
            <ListOrdered className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2">
            <span className="grid h-6 w-6 place-items-center rounded-md bg-sky-500/20 text-sky-400 shadow-[0_0_14px_rgba(56,165,255,0.5)]"><Store className="h-3.5 w-3.5" /></span>
            <p className="font-wordmark text-lg leading-none tracking-tight text-white">PEPSWORLD UK.</p>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={load} data-testid="analytics-refresh" className="grid h-9 w-9 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-white/5" title="Refresh"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={logout} data-testid="admin-logout" className="grid h-9 w-9 place-items-center rounded-lg text-slate-300 transition-colors hover:bg-white/5" title="Sign out"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-3xl px-4 py-6">
        {loading || !data ? (
          <div className="flex items-center justify-center p-24">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-sky-500/30 border-t-sky-400" />
          </div>
        ) : (
          <>
            {/* Today */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
              <h2 className="mb-3 text-lg font-semibold text-white">Today</h2>
              <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-5 shadow-[0_20px_60px_-30px_rgba(56,165,255,0.5)] backdrop-blur-sm" data-testid="today-card">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent" />
                <div className="grid grid-cols-3 divide-x divide-white/10">
                  <div className="pr-3"><TodayStat label="Gross volume" value={today.gross_volume} money align="text-left" /></div>
                  <div className="px-3"><TodayStat label="Payments" value={today.payments} align="text-center" /></div>
                  <div className="pl-3"><TodayStat label="Customers" value={today.customers} align="text-right" /></div>
                </div>
              </div>
            </motion.div>

            {/* Reports overview */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={1}>
              <h2 className="mb-3 mt-8 text-lg font-semibold text-white">Reports overview</h2>
              <div className="border-b border-white/5 pb-2">
                <RangeTabs value={range} onChange={setRange} />
              </div>
            </motion.div>

            <div className="mt-6 space-y-8">
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-sm">
                <ReportChart title="Gross volume" metric={gross} gradId="grossGrad" />
              </motion.div>
              <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-sm">
                <ReportChart title="Net volume from sales" metric={net} gradId="netGrad" />
              </motion.div>
            </div>

            {/* Best sellers */}
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10" data-testid="bestsellers-card">
              <div className="mb-4 flex items-end justify-between">
                <h2 className="text-lg font-semibold text-white">Best sellers</h2>
                <p className="text-xs text-slate-400">{totalUnits} sold · {gross.label}</p>
              </div>
              <div className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-sm">
                {bestSellers.length === 0 ? (
                  <p data-testid="bestsellers-empty" className="py-6 text-center text-sm text-slate-500">No sales in this period yet.</p>
                ) : (
                  bestSellers.map((s, i) => {
                    const max = Math.max(...bestSellers.map((x) => x.units)) || 1;
                    return (
                      <div key={s.name} data-testid={`bestseller-${i}`}>
                        <div className="flex items-end justify-between gap-3">
                          <p className="truncate text-sm font-medium text-slate-200"><span className="mr-2 font-mono text-sky-400/70">#{i + 1}</span>{s.name}</p>
                          <p className="shrink-0 text-sm font-semibold text-white">{gbp(s.revenue)} <span className="font-normal text-slate-500">· {s.units} sold</span></p>
                        </div>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
                          <motion.div
                            className="h-full rounded-full bg-gradient-to-r from-sky-500 to-blue-400 shadow-[0_0_12px_rgba(56,165,255,0.7)]"
                            initial={{ width: 0 }}
                            animate={{ width: `${(s.units / max) * 100}%` }}
                            transition={{ duration: 0.8, delay: 0.3 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
}
